import { useNavigate } from "react-router-dom";
import { Reveal, SectionTag, ArrowLink, MaskedLines } from "@/components/Reveal";

const TIERS = [
  { name: "Launch", price: "From ₹75,000", intl: "International: US$1,500+", desc: "Premium digital presence or focused technology implementation." },
  { name: "Scale", price: "From ₹1,50,000", intl: "International: US$3,500+", desc: "Custom platforms, CRM systems and connected workflows." },
  { name: "Enterprise", price: "Custom Proposal", intl: "Scoped individually", desc: "Complex, multi-system and long-term technology engagements." },
];

export default function EngagementsPreview() {
  const navigate = useNavigate();
  return (
    <section
      id="engagements"
      data-testid="engagements-section"
      className="relative scroll-mt-20 overflow-hidden bg-[#0A0906] py-20 sm:py-28"
    >
      <div
        className="pointer-events-none absolute left-1/2 top-[-15%] h-[55vh] w-[80vw] -translate-x-1/2 rounded-full bg-[#D4AF37]/[0.07] blur-[140px]"
        aria-hidden="true"
      />
      <div className="grain-overlay pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <SectionTag index="12">Engagements</SectionTag>
        </Reveal>
        <h2
          data-testid="engagements-headline"
          className="mt-6 max-w-2xl font-serif text-2xl font-normal leading-[1.15] tracking-tight text-slate-100 sm:text-3xl lg:text-4xl"
        >
          <MaskedLines delay={0.1}>
            Built around the
            <br />
            <span className="italic text-transparent bg-gradient-to-r from-[#F0DDA6] via-[#E7CE8B] to-[#C9A227] bg-clip-text">
              scope of your ambition.
            </span>
          </MaskedLines>
        </h2>

        <div data-testid="engagements-tiers" className="mt-14 border-t border-white/10">
          {TIERS.map((t, i) => {
            const featured = i === 1;
            return (
              <Reveal key={t.name} delay={0.08 * i}>
                <button
                  data-testid={`engagement-tier-${t.name.toLowerCase()}`}
                  onClick={() => navigate("/pricing")}
                  className={`group relative grid w-full items-center gap-4 border-b py-9 text-left transition-colors duration-500 sm:grid-cols-[auto_1fr_auto_auto] sm:gap-10 ${
                    featured
                      ? "border-[#D4AF37]/40"
                      : "border-white/10 hover:border-[#D4AF37]/30"
                  }`}
                >
                  {featured && (
                    <span
                      className="pointer-events-none absolute bottom-4 left-0 top-4 w-px bg-gradient-to-b from-[#E7CE8B] via-[#D4AF37]/50 to-transparent"
                      aria-hidden="true"
                    />
                  )}
                  <span
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-px origin-left scale-x-0 bg-gradient-to-r from-[#D4AF37]/60 to-transparent transition-transform duration-700 group-hover:scale-x-100"
                    aria-hidden="true"
                  />
                  <span
                    className={`font-serif text-3xl font-normal leading-none tracking-tight transition-colors duration-700 ${
                      featured ? "text-[#D4AF37]/30" : "text-white/[0.08] group-hover:text-[#D4AF37]/20"
                    } ${featured ? "sm:pl-6" : ""}`}
                    aria-hidden="true"
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className={featured ? "sm:pl-0" : ""}>
                    <span className="block font-serif text-xl font-normal tracking-tight text-slate-100 transition-colors duration-500 group-hover:text-white sm:text-2xl">
                      {t.name}
                    </span>
                    <span className="mt-2 block max-w-md text-sm font-normal leading-relaxed text-stone-400">
                      {t.desc}
                    </span>
                  </span>
                  <span className="sm:text-right">
                    <span className="block font-serif text-xl font-normal tracking-tight text-transparent bg-gradient-to-r from-[#F0DDA6] via-[#E7CE8B] to-[#C9A227] bg-clip-text sm:text-2xl">
                      {t.price}
                    </span>
                    <span className="mt-1.5 block text-xs font-normal uppercase tracking-[0.15em] text-stone-500">
                      {t.intl}
                    </span>
                  </span>
                  <span className="hidden text-[#E7CE8B] transition-transform duration-500 group-hover:translate-x-1.5 sm:block" aria-hidden="true">
                    →
                  </span>
                </button>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.25}>
          <ArrowLink to="/pricing" testid="engagements-pricing-link">
            View Pricing
          </ArrowLink>
        </Reveal>
      </div>
    </section>
  );
}
